import { useAppSelector } from '@hooks/useRedux';
import {
  selectCanManageMenu,
  selectUserCurrencyPrefix,
} from '@store/auth/authSelectors';
import {
  useAddons,
  useCreateAddon,
  useDeleteAddon,
  useUpdateAddon,
} from '@hooks/menu';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import { Edit2, Loader2, Package, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { Addon, MenuItemStatus } from '../../../types/menu';
import { Button, Input, ReadOnlyAccessNotice } from '../../common';
import { DeleteConfirmModal } from '../departments/DeleteConfirmModal';
import { AddonFormModal } from './AddonFormModal';

export const Addons: React.FC = () => {
  const outletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const canManageMenu = useAppSelector(selectCanManageMenu);
  const currencyPrefix = useAppSelector(selectUserCurrencyPrefix);

  const { data: addons = [], isLoading } = useAddons(outletId);
  const createAddon = useCreateAddon();
  const updateAddon = useUpdateAddon();
  const deleteAddon = useDeleteAddon();
  const isSubmitting =
    createAddon.isPending || updateAddon.isPending || deleteAddon.isPending;

  const [searchQuery, setSearchQuery] = useState('');
  const [isAddonModalOpen, setIsAddonModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState<Addon | null>(null);

  const filteredAddons = addons.filter((addon) =>
    addon.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCreateAddon = (data: {
    name: string;
    default_price: number;
    status: MenuItemStatus;
  }) => {
    if (!outletId || !canManageMenu) return;
    createAddon.mutate(
      { ...data, outlet_id: outletId },
      {
        onSuccess: () => {
          setIsAddonModalOpen(false);
          setSelectedAddon(null);
        },
      },
    );
  };

  const handleUpdateAddon = (data: {
    name: string;
    default_price: number;
    status: MenuItemStatus;
  }) => {
    if (!selectedAddon || !canManageMenu) return;
    updateAddon.mutate(
      { id: selectedAddon.id, ...data },
      {
        onSuccess: () => {
          setIsAddonModalOpen(false);
          setSelectedAddon(null);
        },
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (selectedAddon && canManageMenu) {
      deleteAddon.mutate(selectedAddon.id, {
        onSuccess: () => setSelectedAddon(null),
      });
    }
    setIsDeleteModalOpen(false);
  };

  const openCreateAddon = () => {
    if (!canManageMenu) return;
    setSelectedAddon(null);
    setIsAddonModalOpen(true);
  };

  const openEditAddon = (addon: Addon) => {
    if (!canManageMenu) return;
    setSelectedAddon(addon);
    setIsAddonModalOpen(true);
  };

  const openDeleteAddon = (addon: Addon) => {
    if (!canManageMenu) return;
    setSelectedAddon(addon);
    setIsDeleteModalOpen(true);
  };

  const formatPrice = (price: number) =>
    `${currencyPrefix} ${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price)}`;

  return (
    <div className="space-y-6">
      {!canManageMenu ? (
        <ReadOnlyAccessNotice message="You can view add-ons here, but only authorized roles can create, update, or delete them." />
      ) : null}

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 relative max-w-md">
          <Input
            placeholder="Search addons..."
            value={searchQuery}
            onChange={(value) => setSearchQuery(value.toString())}
          />
        </div>
        {canManageMenu ? (
          <Button onClick={openCreateAddon} disabled={!outletId}>
            <Plus size={16} />
            <span>Create Addon</span>
          </Button>
        ) : null}
      </div>

      {!outletId && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          Select a specific outlet to create a new addon.
        </div>
      )}

      {isLoading && filteredAddons.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 size={32} className="animate-spin text-pos-primary" />
        </div>
      ) : filteredAddons.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Package size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No addons found
          </h3>
          <p className="text-gray-500 mb-6">
            {searchQuery
              ? 'No addons match your search.'
              : canManageMenu
                ? 'Create your first addon to get started.'
                : 'Add-ons created for this outlet will appear here once an authorized user adds them.'}
          </p>
          {!searchQuery && canManageMenu && (
            <button
              type="button"
              onClick={openCreateAddon}
              disabled={!outletId}
              className="inline-flex items-center gap-2 px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={20} />
              <span>Create Addon</span>
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-500">
            <div className="col-span-4">Addon Name</div>
            <div className="col-span-3">Outlet</div>
            <div className="col-span-2">Default Price</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1">Actions</div>
          </div>
          <div className="divide-y divide-gray-100">
            {filteredAddons.map((addon) => (
              <div
                key={addon.id}
                className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-gray-50 transition-colors"
              >
                <div className="col-span-4">
                  <p className="font-medium text-gray-900 text-sm truncate">
                    {addon.name}
                  </p>
                </div>
                <div className="col-span-3 text-sm text-gray-600 truncate">
                  {addon.outlet_name || selectedOutlet?.name || '—'}
                </div>
                <div className="col-span-2 font-medium text-gray-900 text-sm truncate">
                  {formatPrice(addon.default_price)}
                </div>
                <div className="col-span-2">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      addon.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : addon.status === 'out_of_stock'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {addon.status === 'out_of_stock' ? 'OOS' : addon.status}
                  </span>
                </div>
                <div className="col-span-1 flex items-center gap-1">
                  {canManageMenu ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openEditAddon(addon)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openDeleteAddon(addon)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AddonFormModal
        isOpen={isAddonModalOpen}
        onClose={() => {
          setIsAddonModalOpen(false);
          setSelectedAddon(null);
        }}
        onSubmit={selectedAddon ? handleUpdateAddon : handleCreateAddon}
        addon={selectedAddon}
        isLoading={isSubmitting}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedAddon(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Addon"
        message={`Are you sure you want to delete "${selectedAddon?.name}"? This action cannot be undone.`}
        isLoading={isSubmitting}
      />
    </div>
  );
};
